import { httpClient } from "../http-client";
import { api } from "./api";
import { ApiResponse } from "../../types/responses/api-response";
import {
  UpdateUserLanguageRequest,
  UpdateUserLanguageResponse,
} from "../../types/api/user";

export interface LanguagePreference {
  language: string;
  region?: string;
  timezone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

export interface LanguagePreferenceResponse {
  id: string;
  userId: string;
  language: string;
  region?: string;
  timezone?: string;
  dateFormat?: string;
  timeFormat?: string;
  createdAt: string;
  updatedAt: string;
}

export const languageApi = {
  // Получить языковые настройки пользователя (legacy)
  getUserLanguage: async (): Promise<string> => {
    const response = await api.get<{ language: string }>("/users/language");
    return response.data.language;
  },

  // Обновить языковые настройки пользователя (legacy)
  updateLanguage: async (
    data: UpdateUserLanguageRequest,
  ): Promise<UpdateUserLanguageResponse> => {
    // AD-169: маршрут в бэкенде — PATCH, а не PUT. Метод legacy, вызовов сегодня нет
    // (проверено перечнем), но фантомным глаголом он больше не ходит.
    const response = await api.patch<UpdateUserLanguageResponse>(
      "/users/language",
      data,
    );
    return response.data;
  },

  // Получить языковые настройки пользователя (новый API)
  getLanguagePreference: async (): Promise<
    ApiResponse<LanguagePreferenceResponse>
  > => {
    const response = await httpClient.get("/users/language");
    return response.data as ApiResponse<LanguagePreferenceResponse>;
  },

  // Обновить языковые настройки пользователя (новый API)
  updateLanguagePreference: async (
    data: LanguagePreference,
  ): Promise<ApiResponse<LanguagePreferenceResponse>> => {
    // AD-169: маршрута PUT /users/language в бэкенде нет и не было — вызов уходил в 404,
    // а отказ проглатывался catch'ем в хуке. Существующий маршрут — PATCH, и он принимает
    // только язык; регион и часовой пояс сюда никогда не доезжали.
    const response = await httpClient.patch("/users/language", {
      language: data.language,
    });
    return response.data as ApiResponse<LanguagePreferenceResponse>;
  },

  // Получить доступные языки
  getAvailableLanguages: async (): Promise<
    ApiResponse<
      { code: string; name: string; nativeName: string; flag: string }[]
    >
  > => {
    const response = await httpClient.get("/languages/available");
    return response.data as ApiResponse<
      { code: string; name: string; nativeName: string; flag: string }[]
    >;
  },

  // Переводит текст через backend DeepL proxy (FIX-06: убран прямой Google Translate из браузера)
  translateContent: async (
    content: string,
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<
    ApiResponse<{ translatedText: string; detectedLanguage?: string }>
  > => {
    const response = await httpClient.post("/translation/translate", {
      text: content,
      targetLang: targetLanguage,
      ...(sourceLanguage && { sourceLang: sourceLanguage }),
    });
    const data = response.data as {
      translatedText: string;
      sourceLang: string;
    };
    return {
      success: true,
      data: {
        translatedText: data.translatedText,
        detectedLanguage: data.sourceLang,
      },
    } as ApiResponse<{ translatedText: string; detectedLanguage?: string }>;
  },

  // Переводит массив текстов через backend DeepL proxy
  translateBatch: async (
    texts: string[],
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<
    ApiResponse<{ translatedTexts: string[]; detectedLanguage?: string }>
  > => {
    const response = await httpClient.post("/translation/translate/batch", {
      texts,
      targetLang: targetLanguage,
      ...(sourceLanguage && { sourceLang: sourceLanguage }),
    });
    const data = response.data as Array<{ translatedText: string }>;
    return {
      success: true,
      data: { translatedTexts: data.map((r) => r.translatedText) },
    } as ApiResponse<{ translatedTexts: string[]; detectedLanguage?: string }>;
  },
};

export default languageApi;
